import { useState, useEffect, Fragment } from "react";
import { api } from "../../api/api";
import { useAuth } from "../../auth/AuthContext";
import {
  formatarNomeUpper,
  aplicarMascaraCPF,
  aplicarMascaraTelefone,
  formatarData
} from "../../utils/formatadores";

const ModalHistoricoCliente = ({ cliente, onClose }) => {
  const { user, loading } = useAuth();

  const [historico, setHistorico] = useState([]);
  const [carregandoHistorico, setCarregandoHistorico] = useState(false);
  const [logExpandido, setLogExpandido] = useState(null);
  const [detalhesLogs, setDetalhesLogs] = useState({});
  const [carregandoDetalhe, setCarregandoDetalhe] = useState({});

  const idDoCliente = cliente?._id;
  const nomeCliente = cliente?.nomeCliente || cliente?.nome || "Cliente";

  useEffect(() => {
    if (!idDoCliente) return;

    const carregarHistorico = async () => {
      setCarregandoHistorico(true);
      try {
        const res = await api.get(`/historico/cliente/${idDoCliente}`);
        // Filtra para remover lançamentos puramente financeiros
        const historicoGeral = res.data.filter(
          (log) =>
            log.entityType !== "Financeiro" &&
            log.entityType !== "Parcela" &&
            log.action !== "CRIAR_LANCAMENTO_FINANCEIRO" &&
            log.action !== "PAGAMENTO_PARCELA"
        );
        setHistorico(historicoGeral);
      } catch (error) {
        console.error("Erro ao carregar histórico:", error);
      } finally {
        setCarregandoHistorico(false);
      }
    };

    carregarHistorico();
  }, [idDoCliente]);

  if (!cliente) return null;

  const toggleExpandir = async (logId) => {
    if (logExpandido === logId) {
      setLogExpandido(null);
      return;
    }

    setLogExpandido(logId);

    if (!detalhesLogs[logId]) {
      setCarregandoDetalhe((prev) => ({ ...prev, [logId]: true }));
      try {
        const res = await api.get(`/historico/detalhe/${logId}`);
        setDetalhesLogs((prev) => ({ ...prev, [logId]: res.data }));
      } catch (error) {
        console.error("Erro ao carregar detalhes do registro:", error);
      } finally {
        setCarregandoDetalhe((prev) => ({ ...prev, [logId]: false }));
      }
    }
  };

  const formatarAcao = (log) => {
    let textoAcao = "";

    switch (log.action) {
      case "CADASTRO_CLIENTE":
      case "CRIAR_CLIENTE":
      case "NOVO_CLIENTE":
        textoAcao = "Cadastro do Cliente";
        break;
      case "ATUALIZACAO_CLIENTE":
      case "ATUALIZAR_CLIENTE":
      case "EDITAR_CLIENTE":
        textoAcao = "Atualização do Cliente";
        break;
      case "CHECKIN_ATENDIMENTO":
        textoAcao = "Agendamento / Liberado para Atendimento";
        break;
      case "REALIZAR_ATENDIMENTO":
        textoAcao = "Realizado Atendimento";
        break;
      case "CONSULTA_HISTORICO_CLIENTE":
        textoAcao = "Consulta ao Histórico do Cliente";
        break;
      case "CRIAR_PROCESSO":
      case "NOVO_PROCESSO":
        textoAcao = "Processo Criado";
        break;
      case "ATUALIZAR_PROCESSO":
      case "EDITAR_PROCESSO":
        textoAcao = "Processo Atualizado";
        break;
      case "CRIAR_PETICAO":
      case "NOVA_PETICAO":
        textoAcao = "Petição Criada";
        break;
      case "ATUALIZAR_PETICAO":
      case "EDITAR_PETICAO":
        textoAcao = "Petição Atualizada";
        break;
      case "CRIAR_COMPROMISSO":
      case "NOVO_COMPROMISSO":
        textoAcao = "Compromisso Criado";
        break;
      default:
        textoAcao = log.action ? log.action.replace(/_/g, " ") : "Ação Registrada";
    }

    return formatarNomeUpper(textoAcao);
  };

  const renderizarCerneMapeado = (detalhe) => {
    const { entityType, contexto } = detalhe;

    if (!contexto || Object.keys(contexto).length === 0) {
      return <p className="historico-meta-info">Sem dados detalhados gravados.</p>;
    }

    switch (entityType) {
      
      case "Financeiro":
        return (
          <div className="historico-cerne-box">
            <p><b>Tipo de Lançamento:</b> {contexto.tipo?.toUpperCase() || "N/A"}</p>
            <p><b>Valor Total:</b> {contexto.valorTotal ? contexto.valorTotal.toLocaleString("pt-BR", { style: "currency", currency: "BRL" }) : "N/A"}</p>
            <p><b>Qtd. Parcelas:</b> {contexto.numeroParcelas || contexto.qtdParcelasGeradas || 1}</p>
            {contexto.descricao && <p><b>Descrição:</b> {contexto.descricao}</p>}
          </div>
        );

      case "Parcela":
        return (
          <div className="historico-cerne-box">
            <p><b>Nº Parcela:</b> {contexto.numeroParcela} / {contexto.totalParcelas || "N/A"}</p>
            <p><b>Valor:</b> {contexto.valor ? contexto.valor.toLocaleString("pt-BR", { style: "currency", currency: "BRL" }) : "N/A"}</p>
            <p><b>Status:</b> <span className={`status-tag status-${contexto.status}`}>{contexto.status?.toUpperCase() || "N/A"}</span></p>
            {contexto.dataVencimento && <p><b>Vencimento:</b> {formatarData(contexto.dataVencimento)}</p>}
            {contexto.dataPagamento && <p><b>Pagamento:</b> {formatarData(contexto.dataPagamento)}</p>}
          </div>
        );

      case "Processo":
        return (
          <div className="historico-cerne-box">
            <p><b>Nº Processo:</b> {contexto.numeroProcesso || "Não cadastrado"}</p>
            <p><b>Área do Direito:</b> {contexto.areaDireito || "N/A"}</p>
            <p><b>Fase Atual:</b> {contexto.faseAcao || "N/A"}</p>
            {contexto.advogadoId?.nome && (
              <p><b>Advogado:</b> {formatarNomeUpper(contexto.advogadoId.nome)}</p>
            )}
          </div>
        );

      case "Peticao":
        return (
          <div className="historico-cerne-box">
            <p><b>Status da Petição:</b> {contexto.status?.toUpperCase()}</p>
            <p>
              <b>Advogado/Usuário:</b>{" "}
              {contexto.user?.nome ? formatarNomeUpper(contexto.user.nome) : "N/A"}
            </p>
            {contexto.conteudo && (
              <div style={{ gridColumn: "1 / -1" }}>
                <b>Conteúdo:</b>
                <p className="historico-trecho-texto">{contexto.conteudo.substring(0, 180)}...</p>
              </div>
            )}
          </div>
        );

      case "Compromisso":
        return (
          <div className="historico-cerne-box">
            <p><b>Tipo:</b> {contexto.tipo}</p>
            <p><b>Status:</b> {contexto.status?.toUpperCase()}</p>
            <p><b>Prazo:</b> {formatarData(contexto.dataPrazo)}</p>
            {contexto.descricao && <p><b>Descrição:</b> {contexto.descricao}</p>}
          </div>
        );

      case "Atendimento":
        return (
          <div className="historico-cerne-box">
            <p><b>Status Atendimento:</b> {contexto.status?.toUpperCase()}</p>
            <p><b>Horário Check-in:</b> {contexto.horarioCheckin ? new Date(contexto.horarioCheckin).toLocaleString("pt-BR") : "N/A"}</p>
            <p><b>Descrição:</b> {contexto.descricao?.toUpperCase()}</p>
          </div>
        );

      case "Cliente":
        return (
          <div className="historico-cerne-box">
            <p>
              <b>Cliente:</b>{" "}
              {contexto.nomeCliente ? formatarNomeUpper(contexto.nomeCliente) : "N/A"}
            </p>
            <p>
              <b>CPF:</b>{" "}
              {contexto.cpf ? aplicarMascaraCPF(contexto.cpf) : "Não informado"}
            </p>
            <p>
              <b>Data de Nascimento:</b>{" "}
              {contexto.dataNascimento ? formatarData(contexto.dataNascimento) : "Não informada"}
            </p>
            <p>
              <b>Telefone:</b>{" "}
              {contexto.telefone ? aplicarMascaraTelefone(contexto.telefone) : "Não informado"}
            </p>
            <p><b>E-mail:</b> {contexto.email || "Não informado"}</p>
          </div>
        );

      default:
        return (
          <pre className="historico-json-compact">
            {JSON.stringify(contexto, null, 2)}
          </pre>
        );
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content card" style={{ maxWidth: "900px", width: "90%" }}>
        <div className="modal-header">
          <h2>📋 Histórico de {formatarNomeUpper(nomeCliente)}</h2>
          <button type="button" onClick={onClose} className="btn-cancel">
            ✕ Fechar
          </button>
        </div>

        <div className="modal-body-card">
          {loading ? (
            <p>Carregando permissões...</p>
          ) : !user ? (
            <p>Não autorizado</p>
          ) : carregandoHistorico ? (
            <p>Carregando histórico do cliente...</p>
          ) : historico.length === 0 ? (
            <p>Nenhum registro de histórico operacional encontrado para este cliente.</p>
          ) : (
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th className="historico-icon-col"></th>
                    <th>Data / Hora</th>
                    <th>Ação Realizada</th>
                    <th>Realizado por</th>
                  </tr>
                </thead>
                <tbody>
                  {historico.map((log) => {
                    const estaExpandido = logExpandido === log._id;
                    const detalheAtual = detalhesLogs[log._id];
                    const estaCarregando = carregandoDetalhe[log._id];

                    return (
                      <Fragment key={log._id}>
                        <tr
                          onClick={() => toggleExpandir(log._id)}
                          className="historico-row-clickable"
                        >
                          <td className="historico-icon-col">
                            {estaExpandido ? "▼" : "▶"}
                          </td>
                          <td>{new Date(log.createdAt).toLocaleString("pt-BR")}</td>
                          <td>{formatarAcao(log)}</td>
                          <td>
                            <b>{log.userNome ? formatarNomeUpper(log.userNome) : "SISTEMA"}</b>
                          </td>
                        </tr>

                        {estaExpandido && (
                          <tr>
                            <td colSpan="4" className="historico-detalhes-td">
                              <div className="historico-detalhes-box">
                                {estaCarregando && !detalheAtual ? (
                                  <p className="historico-meta-info">Buscando detalhes...</p>
                                ) : detalheAtual ? (
                                  <>
                                    <h5 className="historico-subtitulo">
                                      Resumo ({detalheAtual.entityType})
                                    </h5>
                                    {renderizarCerneMapeado(detalheAtual)}
                                  </>
                                ) : (
                                  <p className="historico-meta-info">Não foi possível recuperar os detalhes.</p>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ModalHistoricoCliente;